import api from "@/lib/api";
import type {
  Staff,
  StaffCreate,
  StaffUpdate,
} from "@/types/staff/api-types/staff-types";
import type { StaffServicesSummaryResponse } from "@/types/staff/api-types/staff-services-summary-types";
import type { PaginatedResponse } from "@/types/common/api-types";
import { isoDate, toIsoDate } from "@/lib/utils";

const parseApiDate = (value: string | Date | null | undefined): Date | null | undefined => {
  if (value == null || value === "") return value as null | undefined;
  if (value instanceof Date) return value;
  return isoDate(value);
};

const formatApiDate = (value: Date | null | undefined): string | null | undefined => {
  if (value == null) return value;
  if (value instanceof Date) return toIsoDate(value);
  return value;
};

const transformStaffDates = (staff: any): Staff => ({
  ...staff,
  start_date: parseApiDate(staff.start_date) as Date,
  end_date: parseApiDate(staff.end_date) ?? undefined,
  birthday: parseApiDate(staff.birthday) ?? undefined,
  hours: staff.hours || [],
  services: staff.services || [],
});

const serializeStaffDates = (data: StaffCreate | StaffUpdate): any => ({
  ...data,
  start_date: formatApiDate(data.start_date as Date | null | undefined),
  end_date: formatApiDate(data.end_date as Date | null | undefined),
  birthday: formatApiDate(data.birthday as Date | null | undefined),
  hours: data.hours || [],
  services: data.services || [],
});

export const staffApi = {
  getAll: async (params?: {
    page?: number;
    size?: number;
    first_name?: string;
    last_name?: string;
    level_name?: string;
    email?: string;
    level_id?: number;
    active_only?: boolean;
  }): Promise<PaginatedResponse<Staff>> => {
    const response = await api.get<PaginatedResponse<any>>(
      "/staff",
      { params }
    );
    return {
      ...response.data,
      items: response.data.items.map(transformStaffDates),
    };
  },

  getById: async (id: number): Promise<Staff> => {
    const response = await api.get<any>(`/staff/${id}`);
    return transformStaffDates(response.data);
  },

  create: async (data: StaffCreate): Promise<Staff> => {
    const serializedData = serializeStaffDates(data);
    const response = await api.post<any>("/staff", serializedData);
    return transformStaffDates(response.data);
  },

  update: async (id: number, data: StaffUpdate): Promise<Staff> => {
    const serializedData = serializeStaffDates(data);
    const response = await api.put<any>(`/staff/${id}`, serializedData);
    return transformStaffDates(response.data);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/staff/${id}`);
  },

  getServicesSummary: async (): Promise<StaffServicesSummaryResponse> => {
    const response = await api.get<StaffServicesSummaryResponse>(
      "/staff/services-summary"
    );
    return response.data;
  },
};